import UI from "@mile-meeting/mile-ui";

import { useEffect, useRef, useState } from "react";
import {
  ContentContainer,
  FormContainer,
  Title,
  Container,
  ReservationOptions,
} from "../reservation-modal/index.styles";
import {
  Title as TitleInput,
  DateInput,
  Time as TimeInput,
  MeetingRoom as MeetingRoomInput,
  Attendees as AttendeesInput,
  Memo as MemoInput,
  ReservationConfidential,
  EditPermissionCheckbox,
  RepeatCondition,
} from "../reservation-modal/input-forms";
import { useRecoilValue } from "recoil";
import { member, user, workspace } from "recoil/recoilState";
import { getMeetingRoomList, TMeetingRoom } from "apis/meetings/meeting-room";
import { TFormattedAttendee, TReservation } from "apis/meetings/meeting-reservation";
import { useToast } from "hook/useToast";
import { sleep } from "utils/validation";
import { LoadingModal } from "uis/modal";
import useDraggableResizableModal from "hook/useDraggableResizableModal";
import OutWarningModal from "../reservation-modal/out-warning-modal";
import { services } from "./service";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";
import { TRRule } from "uis/modal/repeat-rule-setting-modal/types";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import * as Modal from "uis/modal";
import AlertModal from "uis/modal/alert-modal";
import { set } from "lodash";
import { AmplitudeService } from "utils/amplitude.service";

type ReservationModalProps = {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  defaultDate?: Date;
  reservation?: TReservation;
  orgMeetingRoom?: TMeetingRoom;
  onUpdate?: () => void;
  selectedStartTime?: Date;
  selectedEndTime?: Date;
  onChangeTime?: (startDate: Date, endDate: Date) => void;
  onChangeDate?: (date: Date) => void;
  selectedMeetingRoom?: TMeetingRoom;
  onChangeMeetingRoom?: (meetingRoom?: TMeetingRoom) => void;
  requestClose?: boolean;
  onChangeRequestClose?: (value: boolean) => void;
  onChangeTitle?: (value: string) => void;
  closeCallback?: () => void;
  isAbleReservation?: boolean;
  editOption?: RepeatReservationConditionalOption;
};

//INFO: 수정하기 버튼이 활성화 될 수 있는 조건
const enableEdit = (
  title: string,
  attendees: TFormattedAttendee[],
  minAttendeeLimit: number,
  isAllowViolateMinAttendeeLimit: boolean,
  meetingRoom?: TMeetingRoom,
  isAbleReservation?: boolean
) => {
  if (isAbleReservation === false) return false;
  if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) return false;
  return title?.length > 0 && meetingRoom && attendees.length > 0;
};

const EditRsvModal = ({
  open,
  setOpen,
  defaultDate = new Date(),
  reservation,
  onUpdate,
  orgMeetingRoom,
  selectedStartTime,
  selectedEndTime,
  onChangeDate,
  onChangeTime,
  onChangeMeetingRoom,
  selectedMeetingRoom,
  requestClose,
  onChangeRequestClose,
  onChangeTitle,
  closeCallback,
  isAbleReservation = false,
  editOption,
}: ReservationModalProps) => {
  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);
  const amplitudeService = new AmplitudeService();
  const workspaceInfo = useRecoilValue(workspace);

  const { showToast } = useToast();
  const today = new Date();
  const after30Minutes = new Date();
  after30Minutes.setMinutes(today.getMinutes() + 30);

  const ref = useRef<HTMLDivElement>(null);
  const [title, setTitle] = useState<string>(reservation?.title ?? "");
  const [meetingRooms, setMeetingRooms] = useState<TMeetingRoom[]>([]);

  const [rrule, setRrule] = useState<TRRule | undefined>();
  const memberInfo = useRecoilValue(member);
  const userInfo = useRecoilValue(user);
  const [attendees, setAttendees] = useState<TFormattedAttendee[]>([]);
  const [memo, setMemo] = useState<string>();
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openOutWarningModal, setOpenOutWarningModal] = useState<boolean>(false);
  const isChangedAttendees = useRef<boolean>(false);
  const [modalExit, setModalExit] = useState<boolean>(false);
  const { modalPosition, modalHeight, startDrag, startResize, setModalPosition, setModalHeight } =
    useDraggableResizableModal();
  const [isScheduleEditEnabled, setIsEditScheduleEnabled] = useState<boolean>(false);
  const [availableReservation, setAvailableReservation] = useState<string[]>([]);
  const [unavailableReservation, setUnavailableReservation] = useState<string[]>([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState<boolean>(false);
  const [showAllDuplicateAlert, setShowAllDuplicateAlert] = useState(false);
  const [pastRecurrenceCount, setPastRecurrenceCount] = useState<number | undefined>(undefined);
  const [minAttendeeLimit, setMinAttendeeLimit] = useState<number>(1);
  const [isAllowViolateMinAttendeeLimit, setIsAllowViolateMinAttendeeLimit] = useState<boolean>(false);

  //* INFO : 이미 해당 회의실에서 위반된 예약이라면, 위반 허용을 다시 물어보지 않도록 설정
  const [originIsAllowViolateMinAttendeeLimit, setOriginIsAllowViolateMinAttendeeLimit] = useState<boolean>(false);

  //* INFO : 최소 참석 제한에 막혀 예약을 하지 못하는 경우 확인
  const checkMinAttendeeLimit = () => {
    if (isAbleReservation === false) return;
    if (title && title?.length > 0 && selectedMeetingRoom && attendees.length > 0) {
      if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 제한에 막혀 예약을 하지 못하는 경우
        amplitudeService.logPeakTimeMinAttendanceReservationBlock(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          selectedMeetingRoom?.id ?? "",
          selectedMeetingRoom?.name ?? "",
          "수정",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
      if (attendees.length < minAttendeeLimit && isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 위반 예약 생성시
        amplitudeService.logMinAttendanceExceptionReservation(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          selectedMeetingRoom?.id ?? "",
          selectedMeetingRoom?.name ?? "",
          "수정",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
    }
  };

  useEffect(() => {
    setTitle?.(`${memberInfo?.nickname}의 회의실 예약`);
    setAttendees([
      {
        isVisitor: false,
        id: memberInfo?.id ?? null,
        email: userInfo?.email ?? "",
        picture: memberInfo?.image ?? "",
        name: memberInfo?.nickname ?? "",
      },
    ]);
  }, [memberInfo?.nickname]);

  useEffect(() => {
    if (open && !reservation) {
      onChangeTime?.(
        services.getInitStartTime(new Date().toString(), defaultDate),
        services.getInitEndTime(after30Minutes.toString(), defaultDate)
      );
    }

    if (open) {
      setModalPosition({ x: window.innerWidth - 460, y: 88 });
      setModalHeight(window.innerHeight - 88 - 20);

      getMeetingRoomList(1, [], [], 50).then((res) => {
        setMeetingRooms(res.data?.meetingRoomList ?? []);
      });
    }

    if (reservation && open) {
      const { title, attendees, memo, isPrivate, startTime, endTime, guestsCanModify } = services.getInitInfo(
        reservation,
        defaultDate
      );

      const rrule: TRRule | undefined = reservation.rrule
        ? rruleService.parseRRuleString(reservation.rrule)
        : undefined;

      if (
        reservation.rrule &&
        editOption === RepeatReservationConditionalOption.ALL_SCHEDULES &&
        reservation.recurringStartDate
      ) {
        //* INFO: 모든 일정 수정 시 과거 일정 개수 체크
        setPastRecurrenceCount(
          rruleService.countPastOccurrences(reservation?.rrule, new Date(reservation.recurringStartDate))
        );
      }

      setRrule(rrule);
      setTitle(title);
      setAttendees(attendees);
      setMemo(memo);
      setIsPrivate(isPrivate);
      setIsEditScheduleEnabled(guestsCanModify);
      setModalExit(false);
      onChangeTime?.(new Date(startTime), new Date(endTime));
      isChangedAttendees.current = false;

      //* INFO: 이미 위반된 예약이라면, 위반 허용을 다시 물어보지 않도록 설정
      const minAttendeeLimit = services.getAttendeeLimit(
        selectedMeetingRoom?.attendeeLimits ?? [],
        new Date(startTime),
        new Date(endTime)
      );
      setMinAttendeeLimit(minAttendeeLimit);

      if (attendees.length < minAttendeeLimit) {
        setIsAllowViolateMinAttendeeLimit(true);
        setOriginIsAllowViolateMinAttendeeLimit(true);
      } else {
        setIsAllowViolateMinAttendeeLimit(false);
        setOriginIsAllowViolateMinAttendeeLimit(false);
      }
    }
  }, [open, reservation]);

  useEffect(() => {
    if (!reservation && selectedStartTime === undefined) {
      onChangeTime?.(
        services.getInitStartTime(new Date().toString(), defaultDate),
        services.getInitEndTime(after30Minutes.toString(), defaultDate)
      );
    }
  }, [selectedStartTime]);
  //* 외부에서 모달을 닫을 때
  useEffect(() => {
    if (
      requestClose &&
      selectedStartTime &&
      selectedEndTime &&
      selectedMeetingRoom &&
      services.checkChanges(
        reservation,
        selectedStartTime,
        selectedEndTime,
        selectedMeetingRoom,
        title,
        isPrivate,
        isScheduleEditEnabled,
        isChangedAttendees.current,
        memo
      )
    ) {
      setOpenOutWarningModal(true);
    } else if (requestClose) {
      setOpen?.(false);
      closeCallback?.();
      onChangeRequestClose?.(false);
    }
  }, [requestClose]);

  useEffect(() => {
    //* INFO: 회의실 변경시, Google에서 반복 예약 무제한으로 설정된 경우, 반복 예약 최대 횟수를 200회, 6개월로 제한
    if (!rrule?.endDate && !rrule?.repeatCount) {
      const after180 = new Date();
      after180.setMonth(after180.getMonth() + 6);
      if (rrule) {
        setRrule({ ...rrule, repeatCount: 200, endDate: after180 });
      }
    }
  }, [selectedMeetingRoom]);

  useEffect(() => {
    if (selectedStartTime === undefined)
      onChangeTime?.(
        services.getInitStartTime(reservation?.startTime ?? new Date().toString(), defaultDate),
        services.getInitEndTime(reservation?.endTime ?? after30Minutes.toString(), defaultDate)
      );
  }, [selectedStartTime]);

  useEffect(() => {
    if (!selectedEndTime || !selectedStartTime || !selectedMeetingRoom) {
      setIsAllowViolateMinAttendeeLimit(false);
      setMinAttendeeLimit(1);
      return;
    }
    const sameEndTime =
      selectedEndTime.getHours() === new Date(reservation?.endTime ?? "").getHours() &&
      selectedEndTime.getMinutes() === new Date(reservation?.endTime ?? "").getMinutes();
    const sameStartTime =
      selectedStartTime.getHours() === new Date(reservation?.startTime ?? "").getHours() &&
      selectedStartTime.getMinutes() === new Date(reservation?.startTime ?? "").getMinutes();
    const sameMeetingRoom = selectedMeetingRoom?.id === orgMeetingRoom?.id;

    const limit = services.getAttendeeLimit(
      selectedMeetingRoom?.attendeeLimits ?? [],
      selectedStartTime,
      selectedEndTime
    );

    if (sameEndTime && sameStartTime && sameMeetingRoom && isChangedAttendees.current === false) {
      //* INFO: 기존 예약과 동일할 경우, 위반 허용을 다시 물어보지 않도록 설정
      setIsAllowViolateMinAttendeeLimit(originIsAllowViolateMinAttendeeLimit);
      setMinAttendeeLimit(limit);
      return;
    } else {
      setMinAttendeeLimit(limit);
      //* INFO: 회의실이 바뀌거나, 시간이 바뀌거나 참석자가 바뀌면 기존에 예약 위반된 회의더라도, 다시 위반 모달을 띄우도록 설정
      setIsAllowViolateMinAttendeeLimit(false);
    }
  }, [selectedMeetingRoom, selectedStartTime, selectedEndTime, isChangedAttendees.current]);

  const onHandleEdit = async () => {
    setLoading(true);
    //* INFO: 최소 참석 제한 체크 로그
    checkMinAttendeeLimit();

    if (!enableEdit(title ?? "", attendees, minAttendeeLimit, isAllowViolateMinAttendeeLimit, selectedMeetingRoom)) {
      showToast("필수 입력 항목을 확인해주세요.", "warning");
      setLoading(false);
      return;
    }

    if (editOption === RepeatReservationConditionalOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES && rrule) {
      //* INFO: 이 일정 및 향후 일정 반복 예약 전 중복 체크
      const res = await services.checkRecurringReservationDuplicate(
        defaultDate,
        selectedStartTime ?? new Date(),
        selectedEndTime ?? new Date(),
        rruleService.generateRRuleString(rrule, defaultDate),
        selectedMeetingRoom?.id ?? "",
        editOption,
        reservation?.instanceId
      );
      if (res === null) {
        //* 중복 체크 로직에 오류가 발생한 경우
        showToast("반복 예약 중복 체크 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.", "error");
        return;
      }
      if (res?.available.length === 0 && res.unavailable.length > 0) {
        //* INFO: 모든 예약이 중복되는 경우
        setShowAllDuplicateAlert(true);
        setLoading(false);
        return;
      } else if (res?.unavailable && res?.unavailable.length > 0) {
        //* INFO: 중복되는 예약이 있는 경우 (일부)
        setAvailableReservation(res.available);
        setUnavailableReservation(res.unavailable);
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else {
        //* INFO: 중복되는 예약이 없는 경우
        await editReservation();
        setLoading(false);
      }
    } else if (editOption === RepeatReservationConditionalOption.ALL_SCHEDULES && rrule) {
      //* INFO: 모든 일정 반복 예약 전 중복 체크
      const res = await services.checkRecurringReservationDuplicate(
        new Date(reservation?.recurringStartDate ?? ""),
        selectedStartTime ?? new Date(),
        selectedEndTime ?? new Date(),
        rruleService.generateRRuleString(rrule, new Date(defaultDate ?? "")),
        selectedMeetingRoom?.id ?? "",
        editOption,
        reservation?.instanceId
      );
      if (res === null) {
        //* 중복 체크 로직에 오류가 발생한 경우
        showToast("반복 예약 중복 체크 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.", "error");
        return;
      }
      if (res?.available.length === 0 && res.unavailable.length > 0) {
        //* INFO: 모든 예약이 중복되는 경우
        setShowAllDuplicateAlert(true);
        setLoading(false);
        return;
      } else if (res?.unavailable && res?.unavailable.length > 0) {
        //* INFO: 중복되는 예약이 있는 경우 (일부)
        setAvailableReservation(res.available);
        setUnavailableReservation(res.unavailable);
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else {
        //* INFO: 중복되는 예약이 없는 경우
        await editReservation();
        setLoading(false);
      }
    } else {
      //* INFO: 단일 일정은 바로 예약 진행
      await editReservation();
      setLoading(false);
    }
  };

  const editReservation = async () => {
    let requestMemo = memo ?? "";
    if (
      editOption === RepeatReservationConditionalOption.THIS_SCHEDULE && reservation?.googleEventId &&
      reservation.meetingRoom.id === selectedMeetingRoom?.id &&
      selectedMeetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE"
    ) {
      const original = new DOMParser().parseFromString(reservation.memo ?? "", "text/html");
      const metadata = original.createElement("data");
      original.querySelectorAll("data").forEach((data) => {
        Array.from(data.attributes).forEach((attr) => metadata.setAttribute(attr.name, attr.value));
      });
      if (metadata.hasAttribute("creatorEmail")) {
        metadata.textContent = requestMemo;
        requestMemo = metadata.outerHTML;
      }
    }
    const res = await services.editReservation(
      defaultDate,
      selectedStartTime ?? new Date(),
      selectedEndTime ?? new Date(),
      reservation?.id ?? "",
      title,
      requestMemo,
      isPrivate,
      isScheduleEditEnabled,
      orgMeetingRoom?.id ?? "",
      reservation?.googleEventId ?? "",
      attendees,
      selectedMeetingRoom,
      rrule ? rruleService.generateRRuleString(rrule, defaultDate) : undefined,
      editOption,
      isAllowViolateMinAttendeeLimit
    );

    if (res.success) {
      if (selectedMeetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE") {
        await sleep(4000);
      }
      onUpdate?.();
      showToast("회의실 예약이 수정되었습니다.", "success");
      setOpen?.(false);
    } else {
      showToast(
        res.message ?? "회의실 예약 수정이 실패되었습니다. 다시 시도해주세요.",
        res.status === 409 ? "warning" : "error"
      );
    }
  };

  return (
    <Container
      style={{
        display: open ? "flex" : "none",
        left: `${modalPosition.x}px`,
        top: `${modalPosition.y}px`,
        height: modalHeight,
      }}
      ref={ref}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          height: 40,
          left: 0,
          width: "100%",
          display: "flex",
          cursor: "move",
          padding: "0 20px",
          boxSizing: "border-box",
          justifyContent: "end",
          alignItems: "end",
        }}
        onMouseDown={startDrag}
      >
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={() => {
            if (
              selectedStartTime &&
              selectedEndTime &&
              selectedMeetingRoom &&
              services.checkChanges(
                reservation,
                selectedStartTime,
                selectedEndTime,
                selectedMeetingRoom,
                title,
                isPrivate,
                isScheduleEditEnabled,
                isChangedAttendees.current,
                memo
              )
            ) {
              setModalExit(true);
              setOpenOutWarningModal(true);
            } else setOpen?.(false);
          }}
        />

        <div
          style={{
            position: "absolute",
            top: 12,
            left: 12,
          }}
        >
          <UI.Icon name="move" size={20} color="var(--alias-color-label-assistive)" />
        </div>
      </div>
      <Title>
        <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
          회의실 예약 수정
        </UI.Typography>
      </Title>
      <div className="scroll-point">
        <ContentContainer>
          <FormContainer>
            <TitleInput
              onChange={(value) => {
                setTitle(value);
                onChangeTitle?.(value);
              }}
              value={title}
            />
            <div style={{ display: "flex", gap: 8, flexDirection: "column", width: "100%" }}>
              <DateInput
                value={defaultDate}
                onChange={(value) => {
                  onChangeDate?.(value);
                }}
              />
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <TimeInput
                  isToday={defaultDate ? new Date(defaultDate).toDateString() === new Date().toDateString() : false}
                  startValue={
                    selectedStartTime
                      ? { hour: selectedStartTime?.getHours() ?? 0, minutes: selectedStartTime?.getMinutes() ?? 0 }
                      : undefined
                  }
                  onChangeStart={(hour, minutes) => {
                    onChangeTime?.(
                      new Date(defaultDate.setHours(hour, minutes)),
                      new Date(
                        defaultDate.setHours(selectedEndTime?.getHours() ?? 0, selectedEndTime?.getMinutes() ?? 0)
                      )
                    );
                  }}
                  endValue={
                    selectedEndTime
                      ? { hour: selectedEndTime?.getHours() ?? 0, minutes: selectedEndTime?.getMinutes() ?? 0 }
                      : undefined
                  }
                  onChangeEnd={(hour, minutes) => {
                    onChangeTime?.(
                      new Date(
                        defaultDate.setHours(selectedStartTime?.getHours() ?? 0, selectedStartTime?.getMinutes() ?? 0)
                      ),
                      new Date(defaultDate.setHours(hour, minutes))
                    );
                  }}
                />
              </div>
            </div>
            <RepeatCondition
              rrule={rrule}
              onChange={setRrule}
              selectedDate={defaultDate}
              editOption={editOption}
              minCount={pastRecurrenceCount}
              reservationDate={reservation?.startTime ? new Date(reservation.startTime) : undefined}
            />
            <MeetingRoomInput
              open={open}
              meetingRooms={meetingRooms}
              onChange={onChangeMeetingRoom}
              value={selectedMeetingRoom}
              attendeeCount={attendees.length}
            />

            <AttendeesInput
              open={open}
              value={attendees}
              onChange={(value) => {
                setAttendees(value);
                setIsAllowViolateMinAttendeeLimit(false);
                isChangedAttendees.current = true;
              }}
              maxCapacity={selectedMeetingRoom?.capacity}
              minAttendeeLimit={minAttendeeLimit}
              isAllowViolateMinAttendeeLimit={isAllowViolateMinAttendeeLimit}
              onChangeAllowViolateMinAttendeeLimit={() => {
                setIsAllowViolateMinAttendeeLimit(true);
              }}
            />
          </FormContainer>
          <MemoInput
            value={memo ?? ""}
            onChange={(value) => {
              setMemo(value);
            }}
          />
        </ContentContainer>
        <ReservationOptions>
          <ReservationConfidential value={isPrivate} onChange={setIsPrivate} />
          {reservation?.reservationCreator?.member && reservation?.reservationCreator.member.id === memberInfo?.id && (
            <EditPermissionCheckbox value={isScheduleEditEnabled} onChange={setIsEditScheduleEnabled} />
          )}
        </ReservationOptions>
      </div>

      <UI.Button
        type="solid"
        title="수정하기"
        disabled={
          !enableEdit(
            title ?? "",
            attendees,
            minAttendeeLimit,
            isAllowViolateMinAttendeeLimit,
            selectedMeetingRoom,
            isAbleReservation
          )
        }
        size="large"
        style={{ padding: "12px 0", margin: "auto 40px 0 40px" }}
        onClick={onHandleEdit}
      />
      <div className="resize-down" onMouseDown={startResize} />
      <LoadingModal modalIsOpen={loading} />
      <OutWarningModal
        isEditModal
        modalIsOpen={openOutWarningModal}
        closeModal={() => {
          setOpenOutWarningModal(false);
          onChangeRequestClose?.(false);
        }}
        onChange={() => {
          setOpen?.(false);
          setOpenOutWarningModal(false);
          onChangeRequestClose?.(false);
          if (!modalExit) {
            closeCallback?.();
          }
        }}
      />
      <Modal.ReservationAllConflictModal
        modalOpen={showAllDuplicateAlert}
        closeModal={() => setShowAllDuplicateAlert(false)}
      />
      <Modal.ReservationConflictModal
        modalIsOpen={showDuplicateAlert}
        closeModal={() => setShowDuplicateAlert(false)}
        totalReservationCount={availableReservation.length + unavailableReservation.length}
        enableReservationCount={availableReservation.length}
        disableReservationCount={unavailableReservation.length}
        enableReservationTimes={unavailableReservation.map((item) => new Date(item))}
        onReservation={async () => {
          setShowDuplicateAlert(false);
          setLoading(true);
          await editReservation();
          setLoading(false);
        }}
      />
    </Container>
  );
};

export default EditRsvModal;
